import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';

const ModalContext = createContext();

function ModalProvider({ children }) {
  const [openModals, setOpenModals] = useState({});
  const [modalData, setModalData] = useState({});
  const onCloseCallbacks = useRef({});

  const openModal = useCallback((modalId, data = null, options = {}) => {
    setOpenModals((prev) => ({ ...prev, [modalId]: true }));
    if (data) {
      setModalData((prev) => ({ ...prev, [modalId]: data }));
    }
    if (options.onClose) {
      onCloseCallbacks.current[modalId] = options.onClose;
    }
  }, []);

  const closeModal = useCallback((modalId) => {
    setOpenModals((prev) => ({ ...prev, [modalId]: false }));
    const cb = onCloseCallbacks.current[modalId];
    if (cb) {
      cb(modalData[modalId] || {});
      delete onCloseCallbacks.current[modalId];
    }
  }, [modalData]);

  const isModalOpen = useCallback((modalId) => !!openModals[modalId], [openModals]);

  const getModalData = useCallback((modalId) => modalData[modalId] || null, [modalData]);

  const setModalDataById = useCallback((modalId, data) => {
    setModalData((prev) => ({ ...prev, [modalId]: { ...(prev[modalId] || {}), ...data } }));
  }, []);

  const clearModalData = useCallback((modalId) => {
    setModalData((prev) => {
      const updated = { ...prev };
      delete updated[modalId];
      return updated;
    });
  }, []);

  return (
    <ModalContext.Provider value={useMemo(() => ({
      openModal,
      closeModal,
      isModalOpen,
      getModalData,
      setModalDataById,
      clearModalData,
    }), [openModal, closeModal, isModalOpen, getModalData, setModalDataById, clearModalData])}
    >
      {children}
    </ModalContext.Provider>
  );
}

ModalProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

function useModal() {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
}

export { ModalProvider, useModal };
